import { Col, Row } from 'react-bootstrap';
import LeedCardToolBar from './LeedCardToolBar';
import LeadCardHistory from './LeadHistory/LeadCardHistory';
import { useCallback, useEffect, useState } from 'react';
import LeadEventModal from './LeadEventModal';
import * as apiLead from 'api/apiLeads';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import LeadStatus from '../Leads/LeadRows/LeadStatus';
import NewObjectModal from './NewObjectModal';
import { OffNewSale, type NewSaleType } from 'modules/objects';
import LeadCardStatus from './LeadStatus/LeadCardStatus';
import { OffClientInfo } from 'modules/client';
import { OffTasks } from 'modules/task';
import type { LeadDetailsType, LeadEventAddDataType } from '@econtractor/types';
import type { NewObjectType } from './types';
import { LeadProposalPanel } from 'modules/templates';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess, isSellerUser } from 'utils/helpers/access/hasUserAccess';
import useLeadAcceptedProposal from './model/useLeadAcceptedProposal';

interface LeadCardProps {
    leadId: number | string;
}

interface NewEventState extends LeadEventAddDataType {
    leadId: number;
}

const LeadCard = ({ leadId }: LeadCardProps) => {
    const { t } = useTranslation(['Leads', 'common']);
    const { showToast } = useToast();
    const { auth } = useAuth();
    const [newEvent, setNewEvent] = useState<NewEventState | null>(null);
    const [newObject, setNewObject] = useState<NewObjectType | null>(null);
    const [newSale, setNewSale] = useState<NewSaleType | null>(null);
    const [lead, setLead] = useState<LeadDetailsType | null>(null);
    const [clientInfoId, setClientInfoId] = useState<number | null>(null);
    const [showTasks, setShowTasks] = useState(false);
    const [hasLeadProposal, setHasLeadProposal] = useState(false);
    const [openProposal, setOpenProposal] = useState<(() => void) | null>(null);

    const { acceptedGeneration, loading: acceptedLoading, refreshAccepted } = useLeadAcceptedProposal(leadId);

    const handleRegisterOpen = useCallback((open: (() => void) | null) => {
        setOpenProposal(() => open);
    }, []);

    const fetchLead = async () => {
        const resLead = await apiLead.getLead(leadId);
        if (!resLead?.lead) showToast(t('common:messError'), 'danger');
        setLead(resLead);
    };

    const handleProposalUpdated = async () => {
        await fetchLead();
        await refreshAccepted();
    };

    const createEvent = async (eventPatch?: Partial<LeadEventAddDataType>) => {
        if (!newEvent) return false;
        const { leadId: targetLeadId, ...eventData } = {
            ...newEvent,
            ...(eventPatch || {}),
        };
        const resCreate = await apiLead.addEvent(targetLeadId, eventData);
        await fetchLead();
        if (resCreate) showToast(t('leadEvent.messEventCreated'), 'success');
        else showToast(t('common:messError'), 'danger');
        return resCreate;
    };

    useEffect(() => {
        void fetchLead();
    }, [leadId]);

    return (
        <>
            <NewObjectModal
                newObject={newObject}
                setNewObject={setNewObject}
                acceptedGeneration={acceptedGeneration}
                acceptedLoading={acceptedLoading}
                onRefreshAccepted={refreshAccepted}
            />

            <OffNewSale
                newSale={newSale}
                setNewSale={setNewSale}
                acceptedGeneration={acceptedGeneration}
                acceptedLoading={acceptedLoading}
                onRefreshAccepted={refreshAccepted}
            />

            <LeadEventModal newEvent={newEvent} setNewEvent={setNewEvent} createEvent={createEvent} />

            <OffClientInfo clientId={clientInfoId} setClientId={setClientInfoId} />

            <OffTasks
                show={showTasks}
                onHide={() => setShowTasks(false)}
                leadId={lead?.lead?.ID ?? null}
                contextName={lead?.lead?.clientName ?? null}
            />

            <Row className="lead-card"> 
                <Col className="col-sticky" sm={12}>
                    <div className="lead-card__header">
                        <div className="lead-card__identity">
                            <div className="lead-card__title-row">
                                <h4 className="lead-card__title text-truncate">
                                    {String(lead?.lead?.leadName || '').trim() || lead?.lead?.clientName}
                                </h4>
                                <LeadStatus leadStatus={lead?.lead?.leadStatus} />
                            </div>
                        </div>
                        <div className="lead-card__toolbar">
                            <LeedCardToolBar
                                lead={lead?.lead}
                                setNewEvent={setNewEvent}
                                setNewObject={setNewObject}
                                setNewSale={setNewSale}
                                setClientInfoId={setClientInfoId}
                                onShowTasks={() => setShowTasks(true)}
                                onRefresh={fetchLead}
                                acceptedGeneration={acceptedGeneration}
                            />
                        </div>
                    </div>
                </Col>

                {lead?.lead ? (
                    <>
                        <Col md={7} xl={8} xxl={9}>
                            <LeadCardHistory
                                fetchLead={fetchLead}
                                lead={lead?.lead}
                                events={lead?.events}
                                showLockButton={lead?.lead?.myAccess !== 'view'}
                            />
                            {lead?.lead?.ID ? (
                                <LeadProposalPanel 
                                    canEdit={
                                        lead.lead.myAccess === 'view'
                                            ? false
                                            : isSellerUser(auth?.user) || hasUserAccess(auth?.user, 'proposalCreate')
                                    }
                                    leadId={lead.lead.ID}
                                    leadEmail={String(
                                        lead.lead.clientEmail || lead.lead.client_email || '',
                                    ).trim()}
                                    leadName={lead.lead.clientName || ''}
                                    leadStatus={lead.lead.leadStatus}
                                    objectData={lead.lead}
                                    acceptedGeneration={acceptedGeneration}
                                    acceptedLoading={acceptedLoading}
                                    onLeadUpdated={handleProposalUpdated}
                                    onProposalPresenceChange={setHasLeadProposal}
                                    onRegisterOpen={handleRegisterOpen}
                                />
                            ) : null}
                        </Col>

                        <Col md={5} xl={4} xxl={3}>
                            <LeadCardStatus
                                lead={lead?.lead}
                                statuses={lead?.statuses}
                                newEvent={createEvent}
                                setNewEvent={setNewEvent}
                                setNewObject={setNewObject}
                                setNewSale={setNewSale}
                                createEvent={createEvent}
                                acceptedGeneration={acceptedGeneration}
                                acceptedLoading={acceptedLoading}
                                hasLeadProposal={hasLeadProposal}
                                onOpenProposal={openProposal}
                            />
                        </Col>
                    </>
                ) : (
                    <Col className="py-3">
                        <ESpinner />
                    </Col>
                )}
            </Row>
        </>
    );
};

export default LeadCard;
